import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { BookingSection, BookingOption } from '@dreamai/types';
import MapWithCards, { MapItem } from './MapWithCards';
import MapOptionCard from './MapOptionCard';
import RecommendedBadge from './RecommendedBadge';
import { ReactComponent as CloseIcon } from '../../../icons/close-icon.svg';
import { ReactComponent as TickSelectIcon } from '../../../icons/tick-select.svg';
import { useReplyToActions } from '../../../contexts/ReplyToContext';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import './booking-section-map-cards.scss';

/**
 * Format a numeric price with its ISO 4217 currency code into a display string.
 * e.g. (400, "GBP") → "£400.00", (75, "USD") → "$75.00"
 */
function formatPrice(price: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(price);
  } catch {
    return `${price} ${currencyCode}`;
  }
}

interface BookingSectionMapCardsProps {
  section: BookingSection;
  sectionOptions: BookingOption[];
  columns: { key: string; label: string }[];
  onSelectionChange?: (slug: string, selectedIds: Set<string>) => void;
}

/**
 * Map a booking option's data into MapItem + card props.
 */
function optionToMapItem(
  opt: BookingOption,
  columns: { key: string; label: string }[],
): MapItem {
  const d = (opt.data as Record<string, any>) || {};

  const title = d.name || d.title || opt.id;
  const imageUrl = d.image || d.extra_images?.[0] || undefined;
  const address = d.address || undefined;
  const highlighted = !!d.highlighted;
  const highlightedReason = d.highlighted_reason || undefined;

  // Build fields from visible data columns, excluding ones we handle specially
  const skipKeys = new Set([
    'name',
    'title',
    'image',
    'extra_images',
    'notes',
    'score',
    'address',
    'lat',
    'lng',
    'highlighted',
    'highlighted_reason',
    'direct_website_link',
    'booking_link',
    'price',
    'price_currency',
    'purchase_options',
  ]);

  const fields: { label: string; value: string }[] = [];

  // Add formatted price field
  if (d.price != null && d.price_currency) {
    fields.push({
      label: 'Price',
      value: formatPrice(d.price, d.price_currency),
    });
  } else if (
    Array.isArray(d.purchase_options) &&
    d.purchase_options.length > 0
  ) {
    const lowest = d.purchase_options.reduce(
      (min: any, po: any) => (po.price < min.price ? po : min),
      d.purchase_options[0],
    );
    fields.push({
      label: 'Price',
      value: `From ${formatPrice(lowest.price, lowest.price_currency)}`,
    });
  }

  for (const col of columns) {
    if (skipKeys.has(col.key)) continue;
    const val = d[col.key];
    if (val != null && val !== '') {
      fields.push({ label: col.label, value: String(val) });
    }
  }

  return {
    id: opt.id,
    title,
    imageUrl,
    address,
    lat: d.lat != null ? d.lat : undefined,
    lng: d.lng != null ? d.lng : undefined,
    highlighted,
    highlightedReason,
    fields,
  };
}

const BookingSectionMapCards: React.FC<BookingSectionMapCardsProps> = ({
  section,
  sectionOptions,
  columns,
  onSelectionChange,
}) => {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [selectedPurchaseOptions, setSelectedPurchaseOptions] = useState<
    Set<string>
  >(new Set());
  const {
    addReplyTo,
    removeReplyTo,
    registerRemoveCallback,
    unregisterRemoveCallback,
  } = useReplyToActions();

  // Deselect purchase option when its replyTo item is removed via the X button
  useEffect(() => {
    const key = `BookingSectionMapCards:${section.slug}`;
    registerRemoveCallback(key, (id: string) => {
      if (id.startsWith('po:')) {
        const poKey = id.slice(3); // strip 'po:' prefix
        setSelectedPurchaseOptions((prev) => {
          if (!prev.has(poKey)) return prev;
          const next = new Set(prev);
          next.delete(poKey);
          return next;
        });
      }
    });
    return () => unregisterRemoveCallback(key);
  }, [section.slug, registerRemoveCallback, unregisterRemoveCallback]);

  const togglePurchaseOption = useCallback(
    (poKey: string) => {
      setSelectedPurchaseOptions((prev) => {
        const next = new Set(prev);
        if (next.has(poKey)) {
          next.delete(poKey);
          removeReplyTo(`po:${poKey}`);
        } else {
          next.add(poKey);
          const [optId, poIdx] = poKey.split(':');
          const sourceOpt = sectionOptions.find((o) => o.id === optId);
          if (sourceOpt) {
            const sd = (sourceOpt.data as Record<string, any>) || {};
            const optTitle = sd.name || sd.title || optId;
            const poItem = sd.purchase_options?.[Number(poIdx)];
            const poLabel = poItem?.label || `Option ${Number(poIdx) + 1}`;
            addReplyTo({
              id: `po:${poKey}`,
              actionLine: optTitle,
              descriptionLine: poLabel,
              extraContext: `booking_option_id:${optId}, purchase_option:${poLabel}`,
              bookable: true,
              price: poItem?.price != null ? Number(poItem.price) : undefined,
              price_currency: poItem?.price_currency || undefined,
            });
          }
        }
        return next;
      });
    },
    [sectionOptions, addReplyTo, removeReplyTo],
  );

  // Convert booking options to generic MapItems
  const mapItems: MapItem[] = useMemo(
    () => sectionOptions.map((opt) => optionToMapItem(opt, columns)),
    [sectionOptions, columns],
  );

  // Convert section POIs to the shared PointOfInterest format
  const pointsOfInterest = useMemo(
    () =>
      (section.pointsOfInterest || []).map((poi) => ({
        label: poi.label,
        address: poi.address,
        lat: poi.lat,
        lng: poi.lng,
      })),
    [section.pointsOfInterest],
  );

  const renderCard = useCallback(
    (
      item: MapItem,
      cardProps: {
        isFocused: boolean;
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onView: () => void;
        cardRef: (el: HTMLDivElement | null) => void;
      },
    ) => (
      <div
        key={item.id}
        ref={cardProps.cardRef}
        className={cardProps.isFocused ? 'card-focus-flash' : ''}
        onMouseEnter={cardProps.onMouseEnter}
        onMouseLeave={cardProps.onMouseLeave}
      >
        <MapOptionCard
          id={item.id}
          title={item.title}
          imageUrl={item.imageUrl}
          address={item.address}
          highlighted={item.highlighted}
          highlightedReason={item.highlightedReason}
          fields={item.fields}
          onView={cardProps.onView}
        />
      </div>
    ),
    [],
  );

  const renderDetailView = useCallback(
    (item: MapItem, onClose: () => void) => {
      const opt = sectionOptions.find((o) => o.id === item.id);
      if (!opt) return null;
      const d = (opt.data as Record<string, any>) || {};
      const title = d.name || d.title || opt.id;
      const address = d.address || '';
      const description = d.notes || '';
      const highlighted = !!d.highlighted;
      const highlightedReason = d.highlighted_reason;
      const allImages: string[] = [];
      if (d.image) allImages.push(d.image);
      if (d.extra_images && Array.isArray(d.extra_images)) {
        for (const img of d.extra_images) {
          if (img && !allImages.includes(img)) allImages.push(img);
        }
      }

      return (
        <div className='outer-transparent-wrapper'>
          <div className='view-wrapper'>
            <div className='view-header'>
              <div className='combined-header'>
                <h2>{title}</h2>
                {address && <small>{address}</small>}
              </div>
              <button className='close-button' onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
            {highlighted && <RecommendedBadge reason={highlightedReason} />}

            {Array.isArray(d.purchase_options) &&
              d.purchase_options.length > 0 && (
                <div className='purchase-options-outer'>
                  <p>Select an option to discuss or book</p>
                  <div className='purchase-options'>
                    {d.purchase_options.map((po: any, idx: number) => {
                      const poKey = `${opt.id}:${idx}`;
                      const isPoSelected = selectedPurchaseOptions.has(poKey);
                      return (
                        <div
                          key={idx}
                          className={`purchase-option${isPoSelected ? ' selected' : ''}`}
                          onClick={() => togglePurchaseOption(poKey)}
                        >
                          <div
                            className={`booking-select-checkbox${isPoSelected ? ' selected' : ''}`}
                            role='button'
                            tabIndex={0}
                          >
                            <TickSelectIcon />
                          </div>
                          <div className='purchase-option-header'>
                            <span className='purchase-option-label'>
                              {po.label}
                            </span>
                            {po.description && (
                              <span className='purchase-option-desc'>
                                {po.description}
                              </span>
                            )}
                          </div>
                          <span className='purchase-option-price'>
                            {formatPrice(po.price, po.price_currency)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            {description && (
              <div className='view-description'>{description}</div>
            )}
            {allImages.length > 0 && (
              <>
                <div className='image-gallery'>
                  {allImages.map((url, idx) => (
                    <div
                      key={idx}
                      className='image-gallery-thumb'
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img
                        src={url}
                        alt={`${title} ${idx + 1}`}
                        referrerPolicy='no-referrer'
                      />
                    </div>
                  ))}
                </div>
                <Lightbox
                  open={lightboxIndex >= 0}
                  close={() => setLightboxIndex(-1)}
                  index={lightboxIndex}
                  slides={allImages.map((url) => ({ src: url }))}
                />
              </>
            )}
          </div>
        </div>
      );
    },
    [
      sectionOptions,
      selectedPurchaseOptions,
      togglePurchaseOption,
      lightboxIndex,
    ],
  );

  return (
    <MapWithCards
      items={mapItems}
      pointsOfInterest={pointsOfInterest}
      renderCard={renderCard}
      renderDetailView={renderDetailView}
      className='booking-map-cards'
    />
  );
};

export default BookingSectionMapCards;
